import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Загрузчик списка номенклатуры.
 *
 * Список — справочная часть расчёта: названия, плотность утеплителя, площадь,
 * нормы погрузки, категории. Цены в нём тоже есть, но в расчёт они идут не
 * отсюда: суммы считает /api/filter при каждом пересчёте.
 *
 * - Список перечитывается сам (14.10.2026, Роман: «мне всем менеджерам нужно
 *   сказать чтобы перезагружали компы?»). До этого он грузился один раз при
 *   открытии расчёта, и правка в справочнике не доезжала до менеджера, пока тот
 *   не переоткроет приложение. Получалось расхождение на одном экране: цена
 *   свежая, потому что расчёт пересчитали, а плотность — та, что была утром.
 *
 * Перечитываем по двум поводам: менеджер вернулся на вкладку и по таймеру.
 * Оба случая прикрыты паузой MIN_PAUZA_MS: расчёт открыт весь день, и дёргать
 * сервер на каждое переключение вкладок незачем. Сам сервер отдаёт список из
 * кэша, который обновляет раз в 7 минут, — чаще этого запрашивать бессмысленно.
 */

// Не перечитывать чаще, чем раз в 7 минут — с тем же шагом обновляется кэш сервера.
const MIN_PAUZA_MS = 7 * 60 * 1000;
// Фоновая проверка: открытая, но не тронутая вкладка тоже должна освежаться.
const INTERVAL_MS = 10 * 60 * 1000;

function UniqueNomenclature({ onDataLoaded, region }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

  // Момент последней УДАЧНОЙ загрузки: по нему считается пауза. Ошибка его не
  // двигает, иначе после сбоя список ждал бы следующего окна впустую.
  const posledniyaZagruzka = useRef(0);
  // Колбэк родителя меняется каждый рендер, а перезапрос от этого зависеть не должен.
  const soobshchitRoditelyu = useRef(onDataLoaded);
  soobshchitRoditelyu.current = onDataLoaded;

  const zagruzit = useCallback(async ({ povtornaya } = {}) => {
    // Пустая строка — валидное значение: означает относительные запросы к своему
    // же домену (калькулятор доступен сразу на нескольких доменах). Ошибкой
    // считается только реально не заданная переменная.
    if (PATH_TO_BACKEND === undefined || PATH_TO_BACKEND === null) {
      setError("Переменная PATH_TO_BACKEND не определена");
      setLoading(false);
      return;
    }

    const url = region
      ? `${PATH_TO_BACKEND}/api/uniqueNomenclature?region=${encodeURIComponent(region)}`
      : `${PATH_TO_BACKEND}/api/uniqueNomenclature`;

    try {
      const response = await fetch(url, { mode: 'cors' }); // Явно указываем режим CORS
      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error("Некорректные данные от API: " + JSON.stringify(data));
      }

      posledniyaZagruzka.current = Date.now();
      setError(null);

      // Передаем оба набора данных для разных способов оплаты
      soobshchitRoditelyu.current({
        cash: data.dataCash,
        noncash: data.dataNonCash,
        default: data.data,
      });
    } catch (err) {
      console.error("Fetch error details:", err);
      // Неудачное обновление не стирает уже работающий список: менеджер
      // продолжает считать на том, что загрузилось раньше.
      if (!povtornaya) {
        setError(err.message);
      } else {
        console.log("Не удалось обновить список номенклатуры, остаёмся на прежнем:", err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [PATH_TO_BACKEND, region]);

  // Первая загрузка — при открытии расчёта и при смене региона.
  useEffect(() => {
    zagruzit();
  }, [zagruzit]);

  // Повторные — когда вернулись на вкладку и по таймеру, но не чаще паузы.
  useEffect(() => {
    const obnovit = () => {
      if (Date.now() - posledniyaZagruzka.current < MIN_PAUZA_MS) {
        return;
      }
      zagruzit({ povtornaya: true });
    };

    const naVozvrat = () => {
      if (document.visibilityState === "visible") {
        obnovit();
      }
    };

    document.addEventListener("visibilitychange", naVozvrat);
    window.addEventListener("focus", obnovit);
    const taymer = setInterval(obnovit, INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", naVozvrat);
      window.removeEventListener("focus", obnovit);
      clearInterval(taymer);
    };
  }, [zagruzit]);

  if (loading) return null;
  if (error) {
    console.error("Ошибка загрузки данных:", error);
    return null;
  }

  return null;
}

export default UniqueNomenclature;
